import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthShell } from "@/components/AuthShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

export default function ResetPasswordPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!token) {
      setError(t("auth.resetNoToken"));
      return;
    }
    if (password !== confirm) {
      setError(t("auth.passwordMismatch"));
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("auth.resetDone"));
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || t("auth.resetInvalid"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell title={t("auth.resetTitle")} subtitle={t("auth.resetSubtitle")}>
      <form onSubmit={submit} className="space-y-5" data-testid="reset-form">
        <div className="space-y-2">
          <Label className="text-white/80">{t("auth.newPassword")}</Label>
          <Input
            data-testid="reset-password"
            type="password" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder={t("auth.passwordPh")}
            className="bg-[#0a0a0a] border-white/15 text-white h-11 placeholder:text-white/30"
          />
        </div>
        <div className="space-y-2">
          <Label className="text-white/80">{t("auth.confirmPassword")}</Label>
          <Input
            data-testid="reset-confirm"
            type="password" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder={t("auth.passwordPh")}
            className="bg-[#0a0a0a] border-white/15 text-white h-11 placeholder:text-white/30"
          />
        </div>
        {error && (
          <div data-testid="reset-error" className="rounded-md border border-[#dc143c]/40 bg-[#dc143c]/10 px-4 py-2.5 text-sm text-red-300">{error}</div>
        )}
        <Button
          data-testid="reset-submit"
          type="submit" disabled={loading}
          className="w-full h-11 bg-[#EAB308] text-black font-semibold rounded-md hover:opacity-90 transition-opacity"
        >
          {loading ? t("auth.resetSubmitting") : t("auth.resetSubmit")}
        </Button>
      </form>
      <div className="mt-6 text-center">
        <Link to="/login" className="text-sm text-white/50 hover:text-[#EAB308] transition-colors" data-testid="back-to-login">
          {t("auth.backToLogin")}
        </Link>
      </div>
    </AuthShell>
  );
}
